import { Interview } from '../types';

interface VttEntry {
  startTime: string;
  endTime: string;
  speaker: string;
  text: string;
}

// .vttファイルの内容を解析する関数
export const parseVttFile = (vttContent: string): VttEntry[] => {
  const lines = vttContent.split('\n');
  const entries: VttEntry[] = [];
  let currentEntry: Partial<VttEntry> = {};
  let isInEntry = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // タイムスタンプ行を検出
    if (line.includes(' --> ')) {
      const [startTime, endTime] = line.split(' --> ');
      currentEntry = {
        startTime: startTime.trim(),
        endTime: endTime.trim(),
      };
      isInEntry = true;
      continue;
    }

    // テキスト行（話者と内容）
    if (isInEntry && line) {
      // 話者名とテキストを分離（例: "田中: こんにちは"）
      const speakerMatch = line.match(/^([^:]+):\s*(.*)$/);
      if (speakerMatch) {
        currentEntry.speaker = speakerMatch[1].trim();
        currentEntry.text = speakerMatch[2].trim();
      } else {
        // 話者名がない場合
        currentEntry.speaker = '不明';
        currentEntry.text = line;
      }

      if (currentEntry.startTime && currentEntry.endTime && currentEntry.speaker && currentEntry.text) {
        entries.push(currentEntry as VttEntry);
      }
      isInEntry = false;
      currentEntry = {};
    }
  }

  return entries;
};

// VttEntryの配列からインタビューオブジェクトを生成
export const createInterviewFromVtt = (
  vttEntries: VttEntry[],
  fileName: string,
  uploadedBy: string
): Interview => {
  // トランスクリプトを生成
  const transcript = vttEntries
    .map(entry => `${entry.speaker}: ${entry.text}`)
    .join('\n\n');

  // 話者を抽出（重複除去）
  const speakers = [...new Set(vttEntries.map(entry => entry.speaker))];

  // ファイル名から日付を推測（例: "2024-01-15_meeting.vtt"）
  const dateMatch = fileName.match(/(\d{4}-\d{2}-\d{2})/);
  const date = dateMatch ? dateMatch[1] : new Date().toISOString().split('T')[0];

  // 簡単なサマリーを生成
  const summary = `${speakers.join('、')}による会議記録。全${vttEntries.length}の発言を含む。`;

  return {
    id: generateId(),
    date,
    interviewer: uploadedBy,
    interviewee: speakers.filter(s => s !== uploadedBy).join('、') || '参加者',
    transcript,
    summary,
  };
};

// 簡単なID生成関数
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// VTTファイルからインサイトを自動生成する関数
export const generateInsightsFromTranscript = (interview: Interview): Array<{
  title: string;
  content: string;
  summary: string;
}> => {
  const insights: Array<{
    title: string;
    content: string;
    summary: string;
  }> = [];

  // 簡単なキーワード検出によるインサイト生成
  const transcript = interview.transcript.toLowerCase();

  // 課題やニーズに関するキーワード
  const problemKeywords = ['課題', '問題', '困っている', '悩み', 'トラブル', '改善', '難しい'];
  const solutionKeywords = ['解決', 'ソリューション', '提案', '改善案', '対策', '方法'];
  const positiveKeywords = ['良い', 'すばらしい', '満足', '成功', '効果的', 'メリット'];

  if (problemKeywords.some(keyword => transcript.includes(keyword))) {
    insights.push({
      title: '課題・問題点について',
      content: '会議中で言及された課題や問題点に関する内容を抽出・分析しました。',
      summary: '顧客の抱える課題や現在の問題点について議論されました。',
    });
  }

  if (solutionKeywords.some(keyword => transcript.includes(keyword))) {
    insights.push({
      title: '解決策・提案について',
      content: '提案された解決策やソリューションに関する内容を抽出・分析しました。',
      summary: '課題解決に向けた具体的な提案や解決策が話し合われました。',
    });
  }

  if (positiveKeywords.some(keyword => transcript.includes(keyword))) {
    insights.push({
      title: 'ポジティブなフィードバック',
      content: '肯定的な反応や満足に関するコメントを抽出・分析しました。',
      summary: '顧客からの肯定的なフィードバックや満足度の高い内容がありました。',
    });
  }

  // デフォルトのインサイト
  if (insights.length === 0) {
    insights.push({
      title: '会議サマリー',
      content: '会議全体の内容を要約・分析しました。',
      summary: '重要なポイントや今後のアクションアイテムについて整理されました。',
    });
  }

  return insights;
};
